/**
 * Per-player statistics. Feeds the in-game analysis ("who is overbidding?",
 * "can anyone still catch up?") and the all-time leaderboard.
 */

import type { PlannedRound, ScoreRound, ScoringConfig, Seat } from '@shared/types/game';
import { maxRemainingPoints, seatTotals } from './scoring';

export interface SeatStats {
  seat: number;
  /** Rounds where this seat has both a bid and a trick count. */
  roundsScored: number;
  /** Rounds called exactly right. */
  exact: number;
  /** `exact / roundsScored` as a percentage, 0 when nothing is scored yet. */
  exactPct: number;
  avgBid: number;
  avgTricks: number;
  /**
   * Average bid minus average tricks won. Positive is an overbidder, negative
   * an underbidder — the number that settles arguments at the table.
   */
  overUnderIndex: number;
  bestRound: number | null;
  worstRound: number | null;
  total: number;
}

export function seatStats(rounds: readonly ScoreRound[], seat: number): SeatStats {
  let roundsScored = 0;
  let exact = 0;
  let bidSum = 0;
  let trickSum = 0;
  let total = 0;
  let bestRound: number | null = null;
  let worstRound: number | null = null;

  for (const round of rounds) {
    const entry = round.entries.find((candidate) => candidate.seat === seat);
    if (!entry || entry.bid === null || entry.tricksWon === null || entry.points === null) continue;

    roundsScored += 1;
    if (entry.bid === entry.tricksWon) exact += 1;
    bidSum += entry.bid;
    trickSum += entry.tricksWon;
    total += entry.points;
    bestRound = bestRound === null ? entry.points : Math.max(bestRound, entry.points);
    worstRound = worstRound === null ? entry.points : Math.min(worstRound, entry.points);
  }

  const avgBid = roundsScored === 0 ? 0 : bidSum / roundsScored;
  const avgTricks = roundsScored === 0 ? 0 : trickSum / roundsScored;

  return {
    seat,
    roundsScored,
    exact,
    exactPct: roundsScored === 0 ? 0 : (exact / roundsScored) * 100,
    avgBid,
    avgTricks,
    overUnderIndex: avgBid - avgTricks,
    bestRound,
    worstRound,
    total,
  };
}

export function allSeatStats(rounds: readonly ScoreRound[], playerCount: number): SeatStats[] {
  return Array.from({ length: playerCount }, (_, seat) => seatStats(rounds, seat));
}

export interface CatchUpVerdict {
  seat: number;
  displayName: string;
  total: number;
  /** Points still theoretically collectable from `fromRoundNo` on. */
  maxRemaining: number;
  /**
   * False when this seat cannot reach the current leader's total even by
   * scoring the maximum in every remaining round and the leader scoring
   * nothing more. That is the usual "mathematically out of it" reading — it
   * ignores the possibility of the leader going negative, which would otherwise
   * make almost nobody eliminated and the answer useless.
   */
  canStillWin: boolean;
}

export function catchUpVerdicts(
  rounds: readonly ScoreRound[],
  plan: readonly PlannedRound[],
  fromRoundNo: number,
  seats: readonly Seat[],
  config: ScoringConfig
): CatchUpVerdict[] {
  const totals = seatTotals(rounds, seats.length);
  const maxRemaining = maxRemainingPoints(plan, fromRoundNo, config);
  const leaderTotal = totals.length > 0 ? Math.max(...totals) : 0;

  return seats.map((seat) => {
    const total = totals[seat.seat] ?? 0;
    return {
      seat: seat.seat,
      displayName: seat.displayName,
      total,
      maxRemaining,
      canStillWin: total + maxRemaining >= leaderTotal,
    };
  });
}

/** Aggregate across many games, for the all-time leaderboard. */
export interface ProfileStats {
  profileId: string;
  name: string;
  gamesPlayed: number;
  gamesWon: number;
  winPct: number;
  avgFinalScore: number;
  roundsScored: number;
  exact: number;
  exactPct: number;
  overUnderIndex: number;
}
